import html2canvas from "html2canvas";
import { PDFDocument } from "pdf-lib";
import download from "downloadjs";
import moment from "moment";

const EVENT_LABEL_OPTIONS = [
    { value: "training", label: "Training Class" },
    { value: "trip", label: "Trip" },
    { value: "important", label: "Important Meeting" },
    { value: "event", label: "Event" },
    { value: "management", label: "Management Close" },
];

const EVENT_LABEL_COLORS = {
    training: { name: "blue", bg: "#3d7cff" },
    trip: { name: "yellow", bg: "#e6b422" },
    important: { name: "red", bg: "#e53935" },
    event: { name: "orange", bg: "#f5a623" },
    management: { name: "purple", bg: "#7c6cf0" },
};

const DEFAULT_EVENT_COLOR = { name: "gray", bg: "#6b7280" };

const getEventColor = (event) =>
    EVENT_LABEL_COLORS[event?.label] || DEFAULT_EVENT_COLOR;

const formatEventLabel = (value) => {
    if (!value) return null;
    const match = EVENT_LABEL_OPTIONS.find((option) => option.value === value);
    return match ? match.label : value;
};

const formatLongSidebarDate = (date) =>
    moment(date).format("dddd, D MMMM, YYYY");

const formatEventClock = (timeStr) => {
    if (!timeStr) return null;
    const parsed = moment(timeStr, ["HH:mm", "H:mm", "hh:mm A"], true);
    return parsed.isValid() ? parsed.format("h:mm A") : timeStr;
};

const formatSidebarDetail = (event) => {
    const time = formatEventClock(event.start_time);
    const detail = formatEventLabel(event.label) || event.location || "Event";
    if (time) return `${time} : ${detail}`;
    return detail;
};

const escapeHtml = (value) =>
    String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");

const buildAgendaExportMarkup = (events, fromDate, toDate) => {
    const cards = events
        .map((event) => {
            const color = getEventColor(event);
            const day = moment(event.start).format("D");
            const title = escapeHtml(event.title || "Untitled Event");
            const dateLabel = escapeHtml(formatLongSidebarDate(event.start));
            const detail = escapeHtml(formatSidebarDetail(event));

            return `
                <div class="agenda-pdf-card" style="background:${color.bg}">
                    <div class="agenda-pdf-card__top">
                        <div class="agenda-pdf-card__day">${day}</div>
                        <div class="agenda-pdf-card__title">${title}</div>
                    </div>
                    <div class="agenda-pdf-card__date">${dateLabel}</div>
                    <div class="agenda-pdf-card__detail">${detail}</div>
                </div>
            `;
        })
        .join("");

    const rangeLabel =
        fromDate && toDate
            ? `${moment(fromDate).format("D MMM YYYY")} – ${moment(toDate).format(
                  "D MMM YYYY"
              )}`
            : "";

    return `
        <div class="agenda-pdf-root">
            <div class="agenda-pdf-header">
                <div class="agenda-pdf-header__title">Events Agenda</div>
                ${
                    rangeLabel
                        ? `<div class="agenda-pdf-header__range">${escapeHtml(
                              rangeLabel
                          )}</div>`
                        : ""
                }
            </div>
            <div class="agenda-pdf-grid">${cards}</div>
        </div>
    `;
};

const AGENDA_PDF_STYLES = `
    .agenda-pdf-root {
        box-sizing: border-box;
        width: 1100px;
        padding: 28px;
        background: #111111;
        color: #ffffff;
        font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI",
            "Noto Sans SC", "PingFang SC", "Microsoft YaHei", sans-serif;
    }
    .agenda-pdf-header {
        margin-bottom: 20px;
    }
    .agenda-pdf-header__title {
        font-size: 28px;
        font-weight: 800;
        letter-spacing: 0.02em;
        margin-bottom: 6px;
    }
    .agenda-pdf-header__range {
        font-size: 14px;
        font-weight: 500;
        color: #b0b0b0;
    }
    .agenda-pdf-grid {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 16px;
    }
    .agenda-pdf-card {
        box-sizing: border-box;
        border-radius: 6px;
        padding: 18px 16px 16px;
        color: #ffffff;
        min-height: 168px;
        break-inside: avoid;
    }
    .agenda-pdf-card__top {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        margin-bottom: 12px;
    }
    .agenda-pdf-card__day {
        font-size: 52px;
        font-weight: 700;
        line-height: 0.9;
        letter-spacing: -0.03em;
        flex-shrink: 0;
        min-width: 40px;
    }
    .agenda-pdf-card__title {
        font-size: 14px;
        font-weight: 800;
        letter-spacing: 0.03em;
        text-transform: uppercase;
        line-height: 1.3;
        padding-top: 4px;
        word-break: break-word;
    }
    .agenda-pdf-card__date {
        font-size: 13px;
        font-weight: 500;
        line-height: 1.45;
        opacity: 0.95;
        margin-bottom: 14px;
    }
    .agenda-pdf-card__detail {
        font-size: 12px;
        font-weight: 600;
        line-height: 1.4;
        opacity: 0.92;
    }
`;

const canvasToPngBytes = (canvas) =>
    new Promise((resolve, reject) => {
        canvas.toBlob(async (blob) => {
            if (!blob) {
                reject(new Error("Failed to create image from calendar export"));
                return;
            }
            resolve(new Uint8Array(await blob.arrayBuffer()));
        }, "image/png");
    });

const sliceCanvas = (sourceCanvas, startY, sliceHeight) => {
    const slice = document.createElement("canvas");
    slice.width = sourceCanvas.width;
    slice.height = Math.max(1, Math.floor(sliceHeight));
    const ctx = slice.getContext("2d");
    ctx.fillStyle = "#111111";
    ctx.fillRect(0, 0, slice.width, slice.height);
    ctx.drawImage(
        sourceCanvas,
        0,
        startY,
        sourceCanvas.width,
        slice.height,
        0,
        0,
        slice.width,
        slice.height
    );
    return slice;
};

/**
 * Export agenda events as a PDF matching the on-screen event card design.
 */
const exportAgendaPdf = async ({
    events,
    fromDate,
    toDate,
    fileName = "events-agenda",
}) => {
    if (!events?.length) {
        throw new Error("No events to export");
    }

    const host = document.createElement("div");
    host.setAttribute("aria-hidden", "true");
    host.style.cssText =
        "position:fixed;left:-10000px;top:0;width:1100px;pointer-events:none;z-index:-1;";

    const styleTag = document.createElement("style");
    styleTag.textContent = AGENDA_PDF_STYLES;
    host.appendChild(styleTag);

    const content = document.createElement("div");
    content.innerHTML = buildAgendaExportMarkup(events, fromDate, toDate);
    host.appendChild(content);
    document.body.appendChild(host);

    try {
        const canvas = await html2canvas(content.firstElementChild, {
            backgroundColor: "#111111",
            scale: 2,
            useCORS: true,
            logging: false,
        });

        const pdfDoc = await PDFDocument.create();
        const pageWidth = 842; // landscape A4 width in points
        const maxPageHeight = 595; // landscape A4 height
        const pxPerPoint = canvas.width / pageWidth;
        const maxSlicePx = Math.floor(maxPageHeight * pxPerPoint);

        let offsetY = 0;
        while (offsetY < canvas.height) {
            const sliceHeight = Math.min(maxSlicePx, canvas.height - offsetY);
            const pageCanvas =
                offsetY === 0 && sliceHeight === canvas.height
                    ? canvas
                    : sliceCanvas(canvas, offsetY, sliceHeight);
            const pngBytes = await canvasToPngBytes(pageCanvas);
            const pngImage = await pdfDoc.embedPng(pngBytes);
            const pageHeight = pngImage.height * (pageWidth / pngImage.width);
            const page = pdfDoc.addPage([pageWidth, pageHeight]);
            page.drawImage(pngImage, {
                x: 0,
                y: 0,
                width: pageWidth,
                height: pageHeight,
            });
            offsetY += sliceHeight;
        }

        const pdfBytes = await pdfDoc.save();
        download(pdfBytes, `${fileName}.pdf`, "application/pdf");
    } finally {
        if (host.parentNode) {
            host.parentNode.removeChild(host);
        }
    }
};

export default exportAgendaPdf;
